import React, { FC, useCallback, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { IFormField } from 'modules/core/types';
import { useUncontrolledInput } from 'modules/core/hooks';

import { CheckBox, Input, Label } from './styles';

interface IConfirmCheckBox extends IFormField {
  isVerify?: boolean;
  isSetErrorOnUncheck?: boolean;
  disabled?: boolean;
  checkedColor?: string;
  /** Label colour, when it should differ from the checked box. Defaults to `checkedColor`. */
  labelColor?: string;
  lockWhenDisabled?: boolean;
  /** Show the box checked without writing that into the form value the dealer actually chose. */
  forceChecked?: boolean;
}

export const ConfirmCheckBox: FC<IConfirmCheckBox> = ({
  name,
  rules,
  textLabel,
  changeCallback,
  isVerify,
  isSetErrorOnUncheck = false,
  disabled = false,
  checkedColor,
  labelColor,
  lockWhenDisabled = false,
  forceChecked = false,
}) => {
  const { controlField, hasError, setValue, fieldValue } = useUncontrolledInput(name);
  const [isError, setIsError] = useState<boolean>(hasError);
  const isLocked = lockWhenDisabled && disabled;

  const handleChange = useCallback(
    (e: any) => {
      setValue(name, e.target.checked);
      e.target.checked && setIsError(false);
      isSetErrorOnUncheck && !e.target.checked && setIsError(true);
      changeCallback && changeCallback();
    },
    [changeCallback, isSetErrorOnUncheck, name, setValue],
  );

  const renderConfirmCheckBox = useCallback(
    () => (
      <CheckBox
        hasError={isError}
        isVerify={isVerify}
        checkedColor={checkedColor}
        labelColor={labelColor}
        $locked={isLocked}
      >
        <Input
          className="form-check-input mr-1"
          id={name}
          checked={Boolean(forceChecked) || fieldValue || false}
          onChange={handleChange}
          type="checkbox"
          isVerify={isVerify}
          disabled={disabled}
          $locked={isLocked}
        />
        <Label className="form-check-label" htmlFor={name} $locked={isLocked}>
          {textLabel}
        </Label>
      </CheckBox>
    ),
    [
      checkedColor,
      labelColor,
      fieldValue,
      forceChecked,
      handleChange,
      isError,
      isLocked,
      name,
      textLabel,
      isVerify,
      disabled,
    ],
  );

  useEffect(() => {
    setIsError(hasError);
  }, [hasError]);

  return (
    <Controller
      name={name}
      render={renderConfirmCheckBox}
      control={controlField}
      rules={{
        ...rules,
      }}
    />
  );
};
